// Pure, dependency-free page-geometry constants + calc (RECUR-27304). Split
// out of exportToPdf.ts so consumers that only need the page-height math —
// e.g. the always-mounted usePageBoundaries overlay hook — don't
// transitively pull in the exporter's html2canvas/image-service import
// graph. exportToPdf.ts re-exports getDefaultPageHeightPx so its existing
// importers keep working; this file is a pure move, no behavior change.

// ── Why two page-aspect constants, not one ─────────────────────────────────
// PDF_PAGE_ASPECT (below) and SERVER_CONTENT_ASPECT (further down) both
// answer "how tall is one page", so they can look like an accidental
// duplicate — they aren't. Each is the SSOT for a DIFFERENT renderer:
// PDF_PAGE_ASPECT is exact A3 point-math for the RASTER (html2canvas slice)
// export path; SERVER_CONTENT_ASPECT is an empirically-measured aspect for
// the SERVER (headless-Chromium `@page`) path, calibrated against the real
// dev render Lambda. Chromium's scale-to-fit fills an A3 sheet differently
// than a raster slice, so the same page yields two different usable-height
// numbers ON PURPOSE — collapsing them into one constant would mispaginate
// whichever pipeline lost its own number. This file remains the single
// source for BOTH: exportToPdf.ts re-exports getDefaultPageHeightPx from
// here rather than defining its own.

// A3 portrait — wider than A4 so the 1229px editor content isn't crushed.
export const PAGE_WIDTH_PT = 842;
export const MARGIN_PT = 24;
export const PDF_CONTENT_WIDTH_PT = PAGE_WIDTH_PT - MARGIN_PT * 2;
// 1143pt = A3 height (1191pt) minus 2 × 24pt margin.
export const PDF_PAGE_ASPECT = 1143 / PDF_CONTENT_WIDTH_PT;

/**
 * The default (non-content-sized) page height in CSS px for a measured
 * content width, at the PDF export's A3 aspect ratio. The exporter's own
 * SSOT for "how tall is one page" — reused (not re-derived) by anything
 * that needs to reason about page boundaries.
 */
export function getDefaultPageHeightPx(contentWidthPx: number): number {
    return contentWidthPx * PDF_PAGE_ASPECT;
}

// ── Server export geometry (RECUR-27304 real-export calibration) ───────────
// TWO different page heights exist because TWO different exporters produce
// the PDF, with TWO different margins:
//   • RASTER (getDefaultPageHeightPx above) — html2canvas snapshot sliced by
//     `planPages`, margin = MARGIN_PT (24pt). This is the catch-fallback path
//     only (exportToPdfServer.ts's own comment: "the live path is the server
//     renderer") — untouched by this fix, still the raster SSOT.
//   • SERVER (below) — exportToPdfServer.ts's headless-Chromium Lambda,
//     native paged-media fragmentation against `@page { size: A3 portrait;
//     margin: 0.6in }` (exportToPdfServer.ts:346). This IS the page the
//     production "Export .pdf" button always renders (both One Pager and
//     Reports).
//
// RECUR-27304 history: this was originally derived the same way as the
// RASTER formula above — a width-scaled aspect ratio. A later pass
// mis-diagnosed a real export's overflow as proof the Lambda renders at a
// FIXED scale independent of content width, and replaced this with a fixed
// A3-mm/margin-derived constant — that "fix" was itself wrong: it made the
// deterministic export insert extra breaks and produce blank pages. A real
// dev render through the ACTUAL Lambda (RECUR-27304, pixel-ruler
// measurement) settled it: at content width 1229px, one A3 page holds
// ~1785px of that content. Chromium DOES scale the content to fit the page
// width — the same assumption the raster path already makes — so the usable
// height in CONTENT px scales WITH the content width; it is not a fixed
// physical value. Blank/overflow pages appear only when a page's content
// exceeds that measured capacity, so the capacity is expressed here as an
// empirically-measured aspect ratio, not an A3-mm/margin derivation.
export const SERVER_CONTENT_ASPECT = 1.4524; // = 1785px measured page capacity ÷ 1229px content width (real dev render, RECUR-27304).

// A small conservative UNDERSHOOT applied on top of the measured aspect
// ratio above. Once exportToPdfServer.ts INJECTS a forced break at every
// model page-start, the only dangerous direction is a model "page" that's
// actually TALLER than Chromium's real usable capacity at that width — that
// triggers an UNPLANNED extra break inside it. 0.98 is itself calibrated
// from the same real export referenced above (gives ~1749px at the One
// Pager's 1229px content width, safely under the ~1785px measured capacity)
// — it may still need re-validation against a wider export corpus (A-20/
// A-24) if pages keep overflowing, or if it turns out to waste too much
// trailing whitespace. Shared by the overlay (draws the seam) and the export
// (injects at the seam) via the SAME getServerPageHeightPx, so they can
// never disagree.
export const SERVER_PAGE_SAFETY_MARGIN = 0.98;

/**
 * The SERVER export's usable page height in CSS px for a measured content
 * width — the `pageBreakModel` fit-loop's page-height basis, undershot by
 * `SERVER_PAGE_SAFETY_MARGIN`. Distinct from `getDefaultPageHeightPx`
 * (raster); see the comment above for why.
 *
 * RECUR-27304: WIDTH-SCALED, calibrated from a real dev render — NOT a fixed
 * `@page`-box physical value (that hypothesis was tried and disproven; see
 * the history comment above). Chromium fits the editor's content to the
 * page width before paginating, so the usable height in content-px grows
 * with the content width, exactly like the raster path's own
 * `PDF_PAGE_ASPECT` already does.
 */
export function getServerPageHeightPx(contentWidthPx: number): number {
    return contentWidthPx * SERVER_CONTENT_ASPECT * SERVER_PAGE_SAFETY_MARGIN;
}
